import axios, { AxiosResponse } from 'axios';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { cloneObj, ellipsizeThis, toLocaleDecimal } from '../../common/utils';
import Spinner from '../../components/Spinner';
import { MEVSummaryInitialDataResponse, SwapperTotalMEVDataResponse } from '../../types';
import './styles.scss';
import { SortBy, SortSwapperBy } from './types';

const RESULT_PER_PAGE = 10;

const Leaderboard = () => {
    const [mevSummaryData, setMevSummaryData] = useState<MEVSummaryInitialDataResponse>([]);
    const [swapperTotalMEVData, setSwapperTotalMEVData] = useState<SwapperTotalMEVDataResponse>([]);
    const [page, setPage] = useState(0);
    const [swapperPage, setSwapperPage] = useState(0);
    const [currentSort, setCurrentSort] = useState<SortBy>("mev_amount_usd");
    const [currentSortOrder, setCurrentSortOrder] = useState<"desc" | "asc">("desc");
    const [swapperCurrentSort, setSwapperCurrentSort] = useState<SortSwapperBy>("mev_amount_usd");
    const [swapperCurrentSortOrder, setSwapperCurrentSortOrder] = useState<"desc" | "asc">("desc");
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);

    const maxPage = useMemo(() => {
        return Math.ceil((mevSummaryData.length) / RESULT_PER_PAGE) - 1;
    }, [mevSummaryData]);

    const pageData = useMemo(() => {
        if(mevSummaryData.length === 0) {
            return [];
        }

        let sorted = cloneObj<MEVSummaryInitialDataResponse>(mevSummaryData);

        switch(currentSort) {
            case "date":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.BLOCK_TIMESTAMP > b.BLOCK_TIMESTAMP? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.BLOCK_TIMESTAMP < b.BLOCK_TIMESTAMP? 1 : -1);
                }
                break;
            case "denom":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.FROM_DENOM > b.FROM_DENOM? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.FROM_DENOM < b.FROM_DENOM? 1 : -1);
                }
                break;
            case "from_amount":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.FROM_AMOUNT > b.FROM_AMOUNT? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.FROM_AMOUNT < b.FROM_AMOUNT? 1 : -1);
                }
                break;
            case "to_amount":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.TO_AMOUNT > b.TO_AMOUNT? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.TO_AMOUNT < b.TO_AMOUNT? 1 : -1);
                }
                break;
            case "mev_amount_usd":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.MEV_AMOUNT_USD > b.MEV_AMOUNT_USD? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.MEV_AMOUNT_USD < b.MEV_AMOUNT_USD? 1 : -1);
                }
                break;
            case "swapper":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.SWAPPER > b.SWAPPER? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.SWAPPER < b.SWAPPER? 1 : -1);
                }
                break;
            case "tx_id":
                if(currentSortOrder === "asc") {
                    sorted.sort((a,b) => a.TX_ID > b.TX_ID? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.TX_ID < b.TX_ID? 1 : -1);
                }
                break;
        }

        return sorted.filter((x, index) => index >= page * RESULT_PER_PAGE && index < (page + 1) * RESULT_PER_PAGE);
    }, [page, mevSummaryData, currentSort, currentSortOrder,]); 

    const swapperMaxPage = useMemo(() => {
        return Math.ceil((swapperTotalMEVData.length) / RESULT_PER_PAGE) - 1;
    }, [swapperTotalMEVData]);

    const swapperPageData = useMemo(() => {
        if(swapperTotalMEVData.length === 0) {
            return [];
        }

        let sorted = cloneObj<SwapperTotalMEVDataResponse>(swapperTotalMEVData);

        switch(swapperCurrentSort) {
            case "swapper":
                if(swapperCurrentSortOrder === "asc") {
                    sorted.sort((a,b) => a.SWAPPER > b.SWAPPER? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.SWAPPER < b.SWAPPER? 1 : -1);
                }
                break;
            case "mev_amount_usd":
                if(swapperCurrentSortOrder === "asc") {
                    sorted.sort((a,b) => a.MEV_AMOUNT_USD > b.MEV_AMOUNT_USD? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.MEV_AMOUNT_USD < b.MEV_AMOUNT_USD? 1 : -1);
                }
                break;
            case "n_tx":
                if(swapperCurrentSortOrder === "asc") {
                    sorted.sort((a,b) => a.N_TX > b.N_TX? 1 : -1);
                }

                else {
                    sorted.sort((a,b) => a.N_TX < b.N_TX? 1 : -1);
                }
                break;
        }

        return sorted.filter((x, index) => index >= swapperPage * RESULT_PER_PAGE && index < (swapperPage + 1) * RESULT_PER_PAGE);
    }, [swapperPage, swapperTotalMEVData, swapperCurrentSort, swapperCurrentSortOrder,]); 

    useEffect(() => {
        const getInitialData = async() => {
            try {
                let [ swapperData, mevInitialRes, ] = await Promise.all([
                    axios.get<any, AxiosResponse<SwapperTotalMEVDataResponse>>('https://node-api.flipsidecrypto.com/api/v2/queries/500ff951-82c0-49b1-8bea-b1b2b5203180/data/latest'),
                    axios.get<any, AxiosResponse<MEVSummaryInitialDataResponse>>('https://node-api.flipsidecrypto.com/api/v2/queries/a65b99c4-2ed2-4d1f-b9f7-067fd607060e/data/latest'),
                ]);

                if(swapperData.data && mevInitialRes.data) {
                    setSwapperTotalMEVData(swapperData.data);
                    setMevSummaryData(mevInitialRes.data);
                }

                else {
                    setHasError(true);
                }
            }

            catch {
                setHasError(true);
            }

            setIsLoading(false);
        }

        getInitialData();
    }, []);


    const increasePage = useCallback(() => {
        let newPage = page + 1;
        if(newPage > maxPage) {
            return;
        }
        setPage(newPage);
    }, [page, maxPage]);

    const decreasePage = useCallback(() => {
        let newPage = page - 1;
        if(newPage < 0) {
            return;
        }
        setPage(newPage);
    }, [page]);

    const increaseSwapperPage = useCallback(() => {
        let newPage = swapperPage + 1;
        if(newPage > swapperMaxPage) {
            return;
        }
        setSwapperPage(newPage);
    }, [swapperPage, swapperMaxPage]);

    const decreaseSwapperPage = useCallback(() => {
        let newPage = swapperPage - 1;
        if(newPage < 0) {
            return;
        }
        setSwapperPage(newPage);
    }, [swapperPage]);

    const sortBy = useCallback((sortBy: SortBy) => {
        if(currentSort === sortBy) {
            setCurrentSortOrder(currentSortOrder === "asc"? "desc" : "asc");
        }

        else {
            setCurrentSort(sortBy);
            setCurrentSortOrder("desc");
        }
    }, [currentSort, currentSortOrder]);

    const sortSwapperBy = useCallback((sortBy: SortSwapperBy) => {
        if(swapperCurrentSort === sortBy) {
            setSwapperCurrentSortOrder(swapperCurrentSortOrder === "asc"? "desc" : "asc");
        }

        else {
            setSwapperCurrentSort(sortBy);
            setSwapperCurrentSortOrder("desc");
        }
    }, [swapperCurrentSort, swapperCurrentSortOrder]);


    return (
        <div className="leaderboard-page">
            
            {
                pageData.length > 0 &&
                <div className="result-table">
                    <div className="table-navigator">
                        <strong>Top MEV Transactions</strong>
                        <div className="d-flex align-items-center">
                            <button onClick={decreasePage}><i className="fa fa-chevron-left"></i></button>
                            <span>{page + 1} / {maxPage + 1}</span>
                            <button onClick={increasePage}><i className="fa fa-chevron-right"></i></button>
                        </div>
                    </div>
                    <table className='table table-striped'>
                        <thead>
                            <tr>
                                <th>
                                    <button onClick={() => sortBy("date")}>
                                        <div>Date</div>
                                        {
                                            currentSort === "date" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("tx_id")}>
                                        <div>TX ID</div>
                                        {
                                            currentSort === "tx_id" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("swapper")}>
                                        <div>Searcher</div>
                                        {
                                            currentSort === "swapper" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("denom")}>
                                        <div>Symbol</div>
                                        {
                                            currentSort === "denom" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("from_amount")}>
                                        <div>From Amount</div>
                                        {
                                            currentSort === "from_amount" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("to_amount")}>
                                        <div>To Amount</div>
                                        {
                                            currentSort === "to_amount" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"} ms-2`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortBy("mev_amount_usd")}>
                                        <div>Profit</div>
                                        {
                                            currentSort === "mev_amount_usd" &&
                                            <i className={`fa fa-arrow-${currentSortOrder === "asc"? "up" : "down"} ms-2`}></i>
                                        }
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                pageData.map((x, index) => (
                                    <tr key={`explorer-data-${index}`}>
                                        <td>{moment(x.BLOCK_TIMESTAMP).format('YYYY-MM-DD')}<br/>{moment(x.BLOCK_TIMESTAMP).format('HH:mm:ss')}</td>
                                        <td><a className='link' href={`/tx/${x.TX_ID}`} rel="noopener noreferrer" target="_blank">{ellipsizeThis(x.TX_ID, 6, 6)}</a></td>
                                        <td><a className='link' href={`/searcher/${x.SWAPPER}`} rel="noopener noreferrer" target="_blank">{ellipsizeThis(x.SWAPPER, 6, 6)}</a></td>
                                        <td>{x.FROM_DENOM}</td>
                                        <td>{toLocaleDecimal(x.FROM_AMOUNT, 2, 2)}</td>
                                        <td>{toLocaleDecimal(x.TO_AMOUNT, 2, 2)}</td>
                                        <td>{toLocaleDecimal(x.MEV_AMOUNT, 2, 2)}<br/>(${toLocaleDecimal(x.MEV_AMOUNT_USD, 2, 2)})</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>

            }

            {
                swapperPageData.length > 0 &&
                <div className="result-table">
                    <div className="table-navigator">
                        <strong>Top MEV Searchers</strong>
                        <div className="d-flex align-items-center">
                            <button onClick={decreaseSwapperPage}><i className="fa fa-chevron-left"></i></button>
                            <span>{swapperPage + 1} / {swapperMaxPage + 1}</span>
                            <button onClick={increaseSwapperPage}><i className="fa fa-chevron-right"></i></button>
                        </div>
                    </div>
                    <table className='table table-striped'>
                        <thead>
                            <tr>
                                <th>
                                    <button onClick={() => sortSwapperBy("swapper")}>
                                        <div>Swapper</div>
                                        {
                                            swapperCurrentSort === "swapper" &&
                                            <i className={`fa fa-arrow-${swapperCurrentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortSwapperBy("n_tx")}>
                                        <div>Number of Transactions</div>
                                        {
                                            swapperCurrentSort === "n_tx" &&
                                            <i className={`fa fa-arrow-${swapperCurrentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                                <th>
                                    <button onClick={() => sortSwapperBy("mev_amount_usd")}>
                                        <div>Total Profit</div>
                                        {
                                            swapperCurrentSort === "mev_amount_usd" &&
                                            <i className={`fa fa-arrow-${swapperCurrentSortOrder === "asc"? "up" : "down"}`}></i>
                                        }
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                swapperPageData.map((x, index) => (
                                    <tr key={`explorer-data-${index}`}>
                                    <td><a className='link' href={`/searcher/${x.SWAPPER}`} rel="noopener noreferrer" target="_blank">{ellipsizeThis(x.SWAPPER, 6, 6)}</a></td>
                                        <td>{toLocaleDecimal(x.N_TX, 0, 0)}</td>
                                        <td>{toLocaleDecimal(x.MEV_AMOUNT_USD, 2, 2)}</td>
                                    </tr>
                                ))
                            }
                        </tbody>
                    </table>
                </div>
            }

            {
                hasError &&
                <div>Error</div>
            }

            <Spinner
                type='pulse'
                mode="dark"
                text="Loading"
                show={isLoading}
                fullScreen
            />  
        </div>
    );
}

export default Leaderboard;